import { Box, Flex, Stack, Tag, Text, useBreakpointValue } from '@chakra-ui/react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { format } from 'date-fns'
import { utcToZonedTime } from 'date-fns-tz'

import { upperFirst } from 'utils/string-utils'

import theme from 'theme'

import type { Match, MatchStatusType, MatchTeam } from 'types'

type TeamProps = {
  type: 'home' | 'away'
  team: MatchTeam
}

export const Team = ({ type, team }: TeamProps) => {
  const abbrevStyle = useBreakpointValue({
    base: {
      ...theme.baseComponents.matchCards.text.headingSmall,
      lineHeight: theme.baseComponents.matchCards.text.headingSmall.fontSize,
    },
    sm: {
      ...theme.baseComponents.matchCards.text.heading,
      lineHeight: theme.baseComponents.matchCards.text.heading.fontSize,
    },
  })

  const abbrev = team.teamShortName ?? team.teamName.substring(0, 2)
  const name = team.teamName

  return (
    <Stack
      flex={3}
      maxW="120px"
      spacing="1px"
      align={type === 'home' ? 'flex-start' : 'flex-end'}
      justify="flex-start"
      overflowX="hidden"
    >
      {type && <Text textStyle="caption2">{upperFirst(type)}</Text>}
      <Text {...abbrevStyle} color={theme.baseColors.text.default}>
        {abbrev}
      </Text>
      <Text
        w="100%"
        textStyle="caption2"
        color={theme.baseColors.secondary.main}
        textAlign={type === 'away' ? 'right' : 'left'}
        isTruncated
      >
        {name}
      </Text>
    </Stack>
  )
}

type TeamsStubProps = { homeTeam?: Match['homeTeam']; awayTeam?: Match['awayTeam'] }

export const TeamsStub = ({ homeTeam, awayTeam }: TeamsStubProps) => (
  <Flex w="100%" direction="row" justify="space-between">
    <Text textStyle="captionBold" color={theme.baseColors.text.default}>
      {homeTeam?.teamShortName ?? '?'}
    </Text>
    <Text textStyle="caption" color={theme.baseColors.text.disabled}>
      vs
    </Text>
    <Text textStyle="captionBold" color={theme.baseColors.text.default}>
      {awayTeam?.teamShortName ?? '?'}
    </Text>
  </Flex>
)

export const Grade = ({ grade }: { grade: Match['gradeName'] }) => (
  <Flex
    h="28px"
    align="center"
    justify="center"
    backgroundColor={theme.colors.gray['200']}
    boxShadow={theme.shadows.inner}
    borderRadius="4px"
    px="16px"
  >
    <Text
      {...theme.baseComponents.matchCards.text.grade}
      color={theme.baseColors.text.disabled}
      textAlign="center"
      noOfLines={2}
      lineHeight="12px"
    >
      {grade}
    </Text>
  </Flex>
)

export const Status = ({ status }: { status: MatchStatusType }) => {
  const statusColor = {
    upcoming: theme.baseColors.secondary.main,
    scheduled: theme.baseColors.secondary.main,
    completed: theme.baseColors.primary.main,
    live: theme.baseColors.danger.main,
    preparing: theme.baseColors.danger.light,
    moved: theme.baseColors.warning.main,
    orphaned: theme.baseColors.danger.main,
    action_required: theme.baseColors.danger.light,
  }
  const statusText = {
    upcoming: status,
    scheduled: status,
    completed: status,
    live: status,
    preparing: status,
    moved: 'Match Moved',
    orphaned: 'No Match',
    action_required: 'Action Required',
  }

  return (
    <Tag
      size="sm"
      borderRadius="full"
      background={statusColor[status]}
      color={theme.colors.white}
      fontSize="10px"
      fontWeight="700"
      textTransform="uppercase"
    >
      {status === 'live' ? (
        <>
          <Box mr="4px" boxSize="6px" borderRadius="full" background={theme.colors.white} />
          <Text>{statusText[status]}</Text>
        </>
      ) : (
        <>
          <Box display={{ base: 'block', sm: 'none' }}>
            <FontAwesomeIcon
              icon={[
                'far',
                status === 'orphaned' ? 'triangle-exclamation' : status === 'scheduled' ? 'clock' : 'check',
              ]}
              color={theme.colors.white}
            />
          </Box>
          <Text display={{ base: 'none', sm: 'block' }}>{statusText[status]}</Text>
        </>
      )}
    </Tag>
  )
}

export const Encoder = ({ encoder }: { encoder: string }) => (
  <Stack direction="row" align="center" spacing="8px">
    <FontAwesomeIcon icon={['far', 'camcorder']} size="xs" color={theme.baseColors.secondary.main} />
    <Text textStyle="caption" color={theme.baseColors.text.disabled} noOfLines={1}>
      {encoder}
    </Text>
  </Stack>
)

type DateTimeProps = {
  streamDate: string
  matchDate: string
  matchDisplayStatus?: MatchStatusType
  timezone: string
  isStub?: boolean
}

export const DateTime = ({ streamDate, matchDate, matchDisplayStatus, timezone, isStub }: DateTimeProps) => {
  const isBaseViewport = useBreakpointValue({ base: true, sm: false })
  const isSmallViewport = useBreakpointValue({ base: true, lg: false })
  const venueDate = utcToZonedTime(streamDate || matchDate, timezone)
  const matchDateActual = utcToZonedTime(matchDate, timezone)
  const matchDateFormatted = format(
    matchDateActual,
    isBaseViewport ? 'eee dd/MM' : isSmallViewport ? 'eee dd/MM/yy' : 'eee do MMM yyyy'
  )
  const matchTime = format(matchDateActual, 'hh:mm a')
  const dateDate = format(
    venueDate,
    isBaseViewport ? 'eee dd/MM' : isSmallViewport ? 'eee dd/MM/yy' : 'eee do MMM yyyy'
  )
  const dateTime = format(venueDate, 'hh:mm a')
  const timeMatches =
    !streamDate || (matchDate && format(new Date(streamDate), 'HH:mm') === format(new Date(matchDate), 'HH:mm'))
  const dateMatches =
    !streamDate ||
    (matchDate && format(new Date(streamDate), 'yyyy-MM-dd') === format(new Date(matchDate), 'yyyy-MM-dd'))

  return (
    <Stack>
      <Stack
        direction={isStub ? 'column' : 'row'}
        alignItems="center"
        justifyContent="center"
        h="100%"
        spacing={isStub ? '0px' : '12px'}
      >
        <Stack direction="row" spacing={isStub || isSmallViewport ? '4px' : '8px'} align="center">
          {!(isStub && isBaseViewport) && (
            <FontAwesomeIcon
              icon={['far', 'clock']}
              size="xs"
              color={timeMatches ? theme.baseColors.secondary.main : theme.baseColors.danger.dark}
            />
          )}
          <Text
            textStyle="caption"
            color={timeMatches ? theme.baseColors.text.disabled : theme.baseColors.danger.dark}
            noOfLines={1}
          >
            {isSmallViewport ? dateTime : `Start${matchDisplayStatus === 'completed' ? 'ed' : 's'} at ${dateTime}`}
          </Text>
        </Stack>
        <Text
          textStyle="caption2"
          color={dateMatches ? theme.baseColors.text.default : theme.baseColors.danger.dark}
          noOfLines={1}
        >
          {dateDate}
        </Text>
        {(!timeMatches || !dateMatches) && (
          <Tag
            size="sm"
            borderRadius="full"
            background={theme.baseColors.danger.main}
            color={theme.colors.white}
            fontSize="10px"
            fontWeight="700"
            textTransform="uppercase"
          >
            Mismatch
          </Tag>
        )}
      </Stack>
      {(!timeMatches || !dateMatches) && (
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="flex-start"
          h="100%"
          spacing={isStub ? '0px' : '12px'}
        >
          <Stack direction="row" spacing={isStub || isSmallViewport ? '4px' : '8px'} align="center">
            {!(isStub && isBaseViewport) && (
              <FontAwesomeIcon icon={['far', 'clock']} size="xs" color={theme.baseColors.secondary.main} />
            )}
            <Text textStyle="caption" color={theme.baseColors.text.disabled} noOfLines={1}>
              {isSmallViewport ? matchTime : `Start${matchDisplayStatus === 'completed' ? 'ed' : 's'} at ${matchTime}`}
            </Text>
          </Stack>
          <Text textStyle="caption2" color={theme.baseColors.text.default} noOfLines={1}>
            {matchDateFormatted}
          </Text>
        </Stack>
      )}
    </Stack>
  )
}
